import { createContext, useContext, useState, useEffect } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { RecaptchaBridge } from '../hooks/useRecaptcha.jsx';
import { apiService } from '../services/api';

const SettingsContext = createContext();


/**
 * Tailwind needs theme colours as "R G B" channels to support opacity
 * modifiers, while plain CSS elsewhere uses the hex form. Site settings supply
 * hex, so both are written and kept in step.
 */
function setThemeColor(name, hex) {
  if (!hex) return;
  const root = document.documentElement;
  root.style.setProperty(`--color-${name}`, hex);

  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(String(hex).trim());
  if (match) {
    const [r, g, b] = match.slice(1).map((part) => parseInt(part, 16));
    root.style.setProperty(`--color-${name}-rgb`, `${r} ${g} ${b}`);
  }
}

export function SettingsProvider({ children }) {
  const [navigationSettings, setNavigationSettings] = useState({
    background_enabled: false,
    background_color: '#FFFFFF',
    background_opacity: 1.0,
    text_theme: 'dark',
    login_enabled: true,
    login_url: '/member/login',
    login_menu_links: [],
  });

  const [settings, setSettings] = useState({
    // General
    site_name: 'The Church Gathered & Scattered',
    site_tagline: 'A Reformed Presbyterian Church in Teaneck, NJ',
    site_url: 'https://cgsnj.org',

    // Branding
    logo_url: '/logo.png',
    logo_dark_url: '/logo-dark.png',
    favicon_url: '/favicon.svg',

    // SEO
    meta_title: 'CGS - The Church Gathered & Scattered',
    meta_description: 'A Reformed Presbyterian church in Teaneck, New Jersey.',
    enable_robots: true,

    // Theme
    theme_primary_color: '#072441',
    theme_accent_color: '#FFE658',
    theme_highlight_color: '#D8EAF8',
    theme_neutral_color: '#E7E4DC',
    theme_heading_font: 'Crimson Pro',
    theme_body_font: 'Work Sans',
    theme_border_radius: '8',

    // Social
    social_facebook: '',
    social_instagram: '',
    social_youtube: '',

    // Integrations
    youtube_channel_id: '',
    google_analytics_id: '',

    // reCAPTCHA
    recaptcha_site_key: '',
    recaptcha_enabled: false,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const [response, navFooterResponse] = await Promise.all([
        apiService.getSettings(),
        apiService.getNavigationFooter(),
      ]);

      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }

      if (navFooterResponse.data?.navigationSettings) {
        const navSettings = navFooterResponse.data.navigationSettings;
        let parsedLinks = navSettings.login_menu_links;
        if (typeof parsedLinks === 'string') {
          try { parsedLinks = JSON.parse(parsedLinks); } catch { parsedLinks = []; }
        }
        setNavigationSettings({
          background_enabled: navSettings.background_enabled ?? false,
          background_color: navSettings.background_color || '#FFFFFF',
          background_opacity: navSettings.background_opacity ?? 1.0,
          text_theme: navSettings.text_theme || 'dark',
          login_enabled: navSettings.login_enabled ?? true,
          login_url: navSettings.login_url || '/member/login',
          login_menu_links: Array.isArray(parsedLinks) ? parsedLinks : [],
        });
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  // Apply theme colors and fonts to CSS variables
  useEffect(() => {
    if (settings.theme_primary_color) {
      setThemeColor('primary', settings.theme_primary_color);
    }
    if (settings.theme_accent_color) {
      setThemeColor('accent', settings.theme_accent_color);
    }
    if (settings.theme_highlight_color) {
      setThemeColor('highlight', settings.theme_highlight_color);
    }
    if (settings.theme_neutral_color) {
      setThemeColor('neutral', settings.theme_neutral_color);
    }
    if (settings.theme_border_radius) {
      document.documentElement.style.setProperty('--border-radius', `${settings.theme_border_radius}px`);
    }
    if (settings.theme_heading_font) {
      document.documentElement.style.setProperty('--font-heading', `'${settings.theme_heading_font}', serif`);
    }
    if (settings.theme_body_font) {
      document.documentElement.style.setProperty('--font-body', `'${settings.theme_body_font}', sans-serif`);
    }
  }, [settings]);

  // Update document title and meta tags
  useEffect(() => {
    if (settings.meta_title) {
      document.title = settings.meta_title;
    }

    // Update favicon if set
    if (settings.favicon_url) {
      const favicon = document.querySelector('link[rel="icon"]');
      if (favicon) {
        favicon.href = settings.favicon_url;
      }
    }

    // Update meta description
    if (settings.meta_description) {
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.name = 'description';
        document.head.appendChild(metaDesc);
      }
      metaDesc.content = settings.meta_description;
    }
  }, [settings.meta_title, settings.meta_description, settings.favicon_url]);

  // Robots meta tag
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'robots';
      document.head.appendChild(meta);
    }
    meta.content = settings.enable_robots ? 'index, follow' : 'noindex, nofollow';
  }, [settings.enable_robots]);

  // Google Analytics
  useEffect(() => {
    const id = settings.google_analytics_id;
    if (!id) return;

    // Avoid injecting duplicate scripts
    if (document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${id}"]`)) return;

    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    script.async = true;
    document.head.appendChild(script);

    const inline = document.createElement('script');
    inline.text = `if (window.location.pathname.startsWith('/admin')) {window['ga-disable-${id}'] = true;}window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${id}');`;
    document.head.appendChild(inline);

    return () => {
      script.remove();
      inline.remove();
    };
  }, [settings.google_analytics_id]);

  // Google Search Console verification
  useEffect(() => {
    const id = settings.google_search_console_id;
    if (!id) return;

    let meta = document.querySelector('meta[name="google-site-verification"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'google-site-verification';
      document.head.appendChild(meta);
    }
    meta.content = id;

    return () => {
      meta.remove();
    };
  }, [settings.google_search_console_id]);

  const value = {
    settings,
    navigationSettings,
    loading,
    error,
    refetch: loadSettings,
  };

  const recaptchaKey = settings.recaptcha_site_key;
  const recaptchaActive = settings.recaptcha_enabled && recaptchaKey;

  return (
    <SettingsContext.Provider value={value}>
      {recaptchaActive ? (
        <GoogleReCaptchaProvider reCaptchaKey={recaptchaKey}>
          <RecaptchaBridge>
            {children}
          </RecaptchaBridge>
        </GoogleReCaptchaProvider>
      ) : (
        children
      )}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}

export default SettingsContext;
